import { SidebarProps } from '@/common/models/ISidebar';
import { useSidebar } from '@/constants/sidebar';
import { ROUTES } from '@/constants/routes';
import { SeedsPlantTypeSelect } from '@/features/seeds/components';
import {
  COTTON_ONLY_PLANT_TYPES,
  SEED_PLANT_TYPES,
} from '@/features/seeds/constants';
import { SubsidiyaRegionSelect } from '@/features/agrosubsidiya/components';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import {
  To,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { RegionSelect } from '../controls/region-select';
import { YearPickerControl } from '../controls/year-picker-control';
import { Button } from '../ui/button';
import { Form } from '../ui/form';

const getPageTitle = (items: SidebarProps[], pathname: string): string => {
  const sortedItems = [
    ...items,
    ...items.flatMap((item) => item.childrens ?? []),
  ].sort((a, b) => b.href.length - a.href.length);
  for (const item of sortedItems) {
    const itemHref = item.href.startsWith('/') ? item.href : `/${item.href}`;
    const isActive =
      pathname === itemHref || pathname.startsWith(`${itemHref}/`);
    if (isActive) return item.title;
  }
  return '';
};

export default function Navbar() {
  const { SIDEBAR } = useSidebar();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    if (!searchParams.get('year')) {
      setSearchParams(
        (prev) => {
          prev.set('year', String(currentYear));
          return prev;
        },
        { replace: true },
      );
    }
  }, []);

  const form = useForm({
    defaultValues: {
      year: searchParams.get('year')
        ? Number(searchParams.get('year'))
        : currentYear,
    },
  });
  const year = useWatch({
    control: form.control,
    name: 'year',
  });
  useEffect(() => {
    setSearchParams(
      (prev) => {
        if (year) {
          prev.set('year', String(year));
        } else {
          prev.delete('year');
        }
        return prev;
      },
      { replace: true },
    );
  }, [year, setSearchParams]);
  // useEffect(() => {
  //   const subscription = form.watch((values) => {
  //     if (values.year) {
  //       setSearchParams(
  //         (prev) => {
  //           prev.set('year', String(values.year));
  //           return prev;
  //         },
  //         { replace: true },
  //       );
  //     }
  //   });
  //   return () => subscription.unsubscribe();
  // }, [form.watch]);

  const location = useLocation();
  const { pathname } = location;
  const isHarvestLocationsPage = pathname.startsWith(
    `/${ROUTES.HARVEST_LOCATIONS}`,
  );
  const isHarvestClustersPage = pathname.startsWith(
    `/${ROUTES.HARVEST_CLUSTERS}`,
  );
  /** Details sahifalari: `/harvest/locations/:placeId`, `/harvest/clusters/:taxNumber`. */
  const isPlaceDetailsPage =
    isHarvestLocationsPage && pathname !== `/${ROUTES.HARVEST_LOCATIONS}`;
  const isClusterDetailsPage =
    isHarvestClustersPage && pathname !== `/${ROUTES.HARVEST_CLUSTERS}`;
  const isDetailsPage = isPlaceDetailsPage || isClusterDetailsPage;
  /** Klasterlar ro'yxati sahifasida sidebar bandidan uzunroq sarlavha turadi. */
  const isClustersListPage = pathname === `/${ROUTES.HARVEST_CLUSTERS}`;

  /** Details'ga o'tgan sahifa manzilini uzatadi — ortga aynan o'sha yerga qaytamiz. */
  const backTo = (location.state as { from?: To } | null)?.from || {
    pathname: `/${isClusterDetailsPage ? ROUTES.HARVEST_CLUSTERS : ROUTES.HARVEST_LOCATIONS}`,
    search: searchParams.toString(),
  };
  const title = isPlaceDetailsPage
    ? 'harvest_location_details'
    : isClusterDetailsPage
      ? 'harvest_cluster_details'
      : isClustersListPage
        ? 'harvest_clusters_page_title'
        : getPageTitle(SIDEBAR, pathname) || 'profile';
  const isSeedsPage = pathname.startsWith(`/${ROUTES.SEEDS}`);
  const isLandAreaPage = pathname.startsWith(`/${ROUTES.LAND_AREA}`);
  const isCosmicMonitoringPage = pathname.startsWith(
    `/${ROUTES.COSMIC_MONITORING}`,
  );
  const isBankPage = pathname.startsWith(`/${ROUTES.BANK}`);
  const isAgrosubsidiyaPage = pathname.startsWith(`/${ROUTES.AGROSUBSIDIYA}`);
  const isHarvestPage = pathname.startsWith(`/${ROUTES.HARVEST}`);
  const isProfilePage =
    pathname.startsWith(`/${ROUTES.PROFILE}`) || pathname === ROUTES.HOME;
  /** Sug'urta sahifasida yil, viloyat va ekin turi tanlovlari turadi. */
  const isInsurancePage = pathname.startsWith(`/${ROUTES.INSURANCE}`);
  const showPlantTypeSelect =
    !isProfilePage &&
    (isSeedsPage ||
      isLandAreaPage ||
      isCosmicMonitoringPage ||
      isBankPage ||
      isInsurancePage ||
      (isHarvestPage && !isHarvestLocationsPage && !isHarvestClustersPage));
  /** Yer maydoni sahifasida faqat paxta bo'yicha ma'lumot bor. */
  const plantTypeOptions = isLandAreaPage
    ? COTTON_ONLY_PLANT_TYPES
    : SEED_PLANT_TYPES;
  /** Bank sahifasida g'alla uchun faqat 2027 yili tanlanadi. */
  const isBankGrain = isBankPage && searchParams.get('plant_type') === '1';
  useEffect(() => {
    if (isBankGrain) {
      form.setValue('year', 2027);
    } else if (Number(form.getValues('year')) > 2026) {
      form.setValue('year', 2026);
    }
  }, [isBankGrain, form]);
  const handleSubsidiyaRegionChange = (value: string | undefined) => {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (value) {
          next.set('region_code', value);
        } else {
          next.delete('region_code');
        }
        next.set('page', '1');
        return next;
      },
      { replace: true },
    );
  };

  return (
    <nav className="w-full px-10 py-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-6">
          {isDetailsPage && (
            <Button
              variant="outline"
              className="gap-1.5 rounded-md border-white/20 bg-card px-2.5 py-1.5 text-sm font-medium text-white"
              onClick={() => navigate(backTo)}
            >
              <ArrowLeft className="size-4 text-violet-600" />
              {t('back')}
            </Button>
          )}
          <h1 className="text-2xl font-semibold text-white">{t(title)}</h1>
        </div>
        <div className="flex items-center gap-3">
          {isAgrosubsidiyaPage && (
            <SubsidiyaRegionSelect
              value={searchParams.get('region_code') || undefined}
              onChange={handleSubsidiyaRegionChange}
            />
          )}
          {isInsurancePage && <RegionSelect />}
          {showPlantTypeSelect && (
            <SeedsPlantTypeSelect plantTypes={plantTypeOptions} />
          )}
          <Form {...form}>
            <YearPickerControl
              name="year"
              control={form.control}
              minYear={isBankGrain ? 2027 : undefined}
              maxYear={isBankGrain ? 2027 : 2026}
              className="rounded-[100px]"
            />
          </Form>
        </div>
      </div>
    </nav>
  );
}
